import { withProtectedMarkdown } from './markdownProtect';
import { getPhoneHref } from '@/utils/phoneUtil';

const URL_PATTERN = /\b((?:https?:\/\/|www\.)[^\s<>()[\]{}*`]+[^\s<>()[\]{}*`.,!?;:])/gi;
const PHONE_PATTERN = /(^|[^\w/+])(\+?\d[\d\s().-]{6,}\d)(?=$|[^\w])/g;

const shouldLinkPhoneNumber = (value: string): boolean => {
	const trimmed = value.trim();
	const digits = trimmed.replace(/\D/g, '');
	if (digits.length < 8 || digits.length > 15) return false;
	if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}$/.test(trimmed)) return false;
	if (/^(?:1[6-9]\d{2}|20\d{2})\s*-\s*(?:1[6-9]\d{2}|20\d{2})$/.test(trimmed)) return false;
	return trimmed.startsWith('+') || /[\s().-]/.test(trimmed) || digits.length >= 9;
};

/**
 * Splits off a trailing legacy "Sources:" prose block so contact linkification never rewrites inside
 * it. The server-normalised answer no longer emits this block; kept tolerant for pre-contract
 * exchanges that still carry it.
 */
const splitSourcesSection = (text: string): { body: string; sources: string } => {
	const sourcesMatch = text.match(/\n+\*{0,2}\s*\*{0,2}Sources?:?\*{0,2}\s*\*{0,2}[\s\S]*/i);
	if (sourcesMatch?.index === undefined) return { body: text, sources: '' };

	return {
		body: text.slice(0, sourcesMatch.index),
		sources: text.slice(sourcesMatch.index),
	};
};

/**
 * Turns bare URLs and phone numbers in the answer body into Markdown links, leaving existing
 * Markdown/code/links untouched (via {@link withProtectedMarkdown}) and skipping any trailing
 * "Sources:" prose block.
 */
export function linkifyContactDetails(text: string): string {
	const { body, sources } = splitSourcesSection(text);
	const linkedBody = withProtectedMarkdown(body, (unprotectedText) =>
		unprotectedText
			.replace(URL_PATTERN, (match) => {
				const href = /^https?:\/\//i.test(match) ? match : `https://${match}`;
				return `[${match}](${href})`;
			})
			.replace(PHONE_PATTERN, (match, prefix: string, phoneNumber: string) => {
				if (!shouldLinkPhoneNumber(phoneNumber)) return match;
				return `${prefix}[${phoneNumber}](${getPhoneHref(phoneNumber)})`;
			}),
	);

	return `${linkedBody}${sources}`;
}
